import React from 'react';
import { useTenant } from '../../../context/TenantContext';
import { useMarketingCourses } from '../MarketingData';

interface DerivedMetric {
  label: string;
  value: string;
  delta?: string;
  intent?: 'go' | 'warn' | 'info';
}

/** Hard-numbers strip. Tenant-supplied metrics win; otherwise derive from real
 *  course data. Never fabricates — omits any metric that can't be derived. */
export const UtilityMetrics: React.FC = () => {
  const { tenant } = useTenant();
  const tenantMetrics = tenant?.branding.marketing?.metrics;
  const { courses, loaded } = useMarketingCourses();

  if (!loaded) return null;

  let metrics: DerivedMetric[];
  if (tenantMetrics && tenantMetrics.length > 0) {
    metrics = tenantMetrics;
  } else {
    metrics = [{ label: 'Programmes', value: String(courses.length) }];
    const instructorNames = new Set(
      courses.map(c => c.instructorName).filter(n => n && n !== 'Unknown'),
    );
    if (instructorNames.size > 0) {
      metrics.push({ label: 'Faculty', value: `${instructorNames.size}` });
    }
    const totalStudents = courses.reduce((s, c) => s + (c.totalStudents || 0), 0);
    if (totalStudents > 0) {
      metrics.push({ label: 'Total students', value: String(totalStudents) });
    }
  }

  if (metrics.length === 0) return null;

  return (
    <section id="metrics" className="util-metrics bg-white border-b border-[var(--util-rule)]">
      <div className="max-w-[1320px] mx-auto px-6">
        <div
          className="grid border-l border-[var(--util-rule)]"
          style={{ gridTemplateColumns: `repeat(${metrics.length}, minmax(0, 1fr))` }}
        >
          {metrics.map((m, i) => (
            <div key={i} className="px-6 py-6 border-r border-[var(--util-rule)]">
              <div className="font-util-mono text-[10px] uppercase tracking-[0.06em] text-[var(--util-ink-faint)] mb-2">
                {m.label}
              </div>
              <div className="font-util-sans text-[28px] font-semibold tracking-[-0.03em] leading-none">
                {m.value}
                {m.delta && (
                  <span
                    className={`font-util-mono text-[12px] ml-1.5 font-medium ${
                      m.intent === 'warn' ? 'text-[#b85a00]' : 'text-[#2f7a3a]'
                    }`}
                  >
                    {m.delta}
                  </span>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};
